<template>
  <!--
    ตั้งค่าเครื่องพิมพ์ของ "เครื่องนี้" (utils/printerSettings.js) — ฮาร์ดแวร์ตั้งที่เครื่อง
    เนื้อหาใบเสร็จ/นโยบายพิมพ์อัตโนมัติยังอยู่ที่ตั้งค่าสาขา

    เปิดได้จากไอคอนเครื่องพิมพ์ที่แถบบน (POS + back-office), การ์ดในหน้าตั้งค่า และหน้าต่างถาม
    ครั้งแรกตอนเข้า POS — layout เป็นคน mount ตาม printerSettingsUi.open
    ต้องตั้งบนเครื่องที่จะพิมพ์เอง เพราะ Chrome ผูกการจับคู่เครื่องพิมพ์ไว้กับเบราว์เซอร์
    บันทึกทันทีที่เปลี่ยน ไม่มีปุ่มบันทึก — ค่าอยู่ในเครื่องนี้ ไม่มีอะไรต้องส่ง server

    ลำดับ = "ค้นหาเครื่องพิมพ์ก่อน": กดค้นหา USB หรือ Bluetooth → เจอเครื่อง = ตั้งช่องทางนั้นเอง
    + เดาขนาดกระดาษจากชื่อรุ่น ("ไม่มีเครื่องพิมพ์" เป็นทางเลือกรอง)
    WebUSB/Web Bluetooth ไม่ยอมให้เว็บเห็นรายชื่ออุปกรณ์เอง (privacy) การค้นหาจึงคือหน้าต่างเลือกของ Chrome

    Teleport ไป body: offcanvas อยู่ใน layout ที่มี sidebar/topbar ซ้อนอยู่ กันโดน stacking
    context ของพวกนั้นกัก (CONVENTIONS §3)
  -->
  <Teleport to="body">
    <div
      ref="offcanvas"
      class="offcanvas offcanvas-end offcanvas-fullscreen-sm-down printer-offcanvas"
      tabindex="-1"
    >
      <div class="offcanvas-header border-bottom bg-white py-3">
        <div>
          <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
            <i class="fa-solid fa-print text-theme"/>
            {{ $t('Printer Settings') }}
          </h6>
          <div class="small text-muted mt-1">
            {{ $t('_printer.For this device only - each till or tablet sets up its own') }}
          </div>
        </div>
        <button
          type="button"
          class="btn-close shadow-none"
          data-bs-dismiss="offcanvas"
          :aria-label="$t('Close')"
        />
      </div>

      <div class="offcanvas-body bg-body-secondary p-3">
        <!-- สถานะรวมของเครื่องนี้ — อ่านแวบเดียวรู้ว่าพร้อมพิมพ์ไหม -->
        <div class="printer-status rounded-3 d-flex align-items-center gap-3 p-3 mb-3" :class="status.classes">
          <div class="printer-status__icon rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
            <i :class="status.icon"/>
          </div>
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="fw-bold">{{ $t(status.title) }}</div>
            <div class="small text-truncate opacity-75">{{ status.sub }}</div>
          </div>
        </div>

        <!-- 1. เครื่องพิมพ์ — ค้นหาก่อนเสมอ -->
        <div class="form-card">
          <div class="form-section-header">
            <i class="fa-solid fa-print"/>
            <span>{{ $t('_printer.Printer') }}</span>
          </div>

          <!-- ยังไม่ได้ต่อเครื่องพิมพ์ (ยังไม่ตั้ง หรือเลือกใช้ PDF ไว้) -->
          <div v-if="!isDirect" class="text-center px-3 py-4">
            <div class="finder-icon rounded-circle bg-theme-subtle text-theme d-flex align-items-center justify-content-center mx-auto mb-3">
              <i class="fa-solid fa-magnifying-glass"/>
            </div>
            <div class="fw-semibold mb-1">
              {{ form.transport === 'none' ? $t('_printer.This device prints receipts as PDF') : $t('_printer.Connect a receipt printer') }}
            </div>
            <template v-if="findOptions.length">
              <div class="small text-muted mb-3 mx-auto" style="max-width: 340px;">
                {{ $t('_printer.Plug in a USB printer or turn on a Bluetooth printer then press Find') }}
              </div>
              <find-printer-buttons
                :options="findOptions"
                :busy="busy"
                @find="handleFind"
              />
            </template>
            <div v-else class="small text-danger-emphasis">
              {{ $t('_printer.This browser cannot connect to a printer - use Chrome on a computer or an Android tablet') }}
            </div>
            <div v-if="form.transport !== 'none'" class="mt-2">
              <button
                type="button"
                class="btn btn-link btn-sm text-muted text-decoration-none"
                @click="form.transport = 'none'"
              >
                <i class="fa-regular fa-file-pdf me-1"/>{{ $t('_printer.This device has no printer - use PDF') }}
              </button>
            </div>
          </div>

          <!-- ต่อเครื่องพิมพ์ตรงแล้ว (USB / Bluetooth) -->
          <template v-else>
            <div class="p-3 d-flex align-items-center gap-3">
              <div
                class="device-avatar rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                :class="device.online ? 'bg-success-subtle text-success-emphasis' : 'bg-body-secondary text-secondary'"
              >
                <i :class="device.icon"/>
              </div>
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="fw-semibold text-truncate">
                  {{ device.name || $t('_printer.No printer found') }}
                </div>
                <div class="small d-flex align-items-center gap-1" :class="device.online ? 'text-success' : 'text-muted'">
                  <span class="status-dot" :class="device.online ? 'bg-success' : 'bg-secondary'"/>
                  {{ $t(device.statusText) }}
                </div>
              </div>
              <!-- Bluetooth ที่หลุด (เปิดแอปใหม่/เครื่องปิด) → ปุ่มหลักคือเชื่อมต่อ ไม่ใช่เปลี่ยนเครื่อง -->
              <button
                v-if="isBt && !bt.connected"
                type="button"
                class="btn btn-sm btn-theme rounded-3 fw-semibold text-nowrap"
                :disabled="busy"
                @click="handleConnectBt"
              >
                <i :class="busy ? 'fa-solid fa-spinner fa-spin' : 'fa-brands fa-bluetooth-b'" class="me-1"/>
                {{ $t('_printer.Connect') }}
              </button>
              <button
                v-else
                type="button"
                class="btn btn-sm btn-outline-theme rounded-3 fw-semibold text-nowrap"
                :disabled="busy"
                @click="toggleChange"
              >
                <i class="fa-solid fa-magnifying-glass me-1"/>{{ $t('_printer.Change printer') }}
                <i v-if="findOptions.length > 1" class="fa-solid ms-1" :class="showChange ? 'fa-chevron-up' : 'fa-chevron-down'"/>
              </button>
            </div>
            <!--
              เปลี่ยนเครื่อง = กางปุ่มค้นหาในการ์ดเลย ไม่ใช้เมนูลอย: .form-card ตั้ง overflow:hidden
              (ให้มุมโค้ง) เมนูที่ลอยออกมาโดนตัดครึ่ง + บังลิงก์ด้านล่าง
            -->
            <div v-if="showChange" class="px-3 pb-3">
              <div class="bg-body-tertiary rounded-3 p-3 text-center">
                <div class="small text-muted mb-2">
                  {{ $t('_printer.Plug in a USB printer or turn on a Bluetooth printer then press Find') }}
                </div>
                <find-printer-buttons
                  :options="findOptions"
                  :busy="busy"
                  small
                  @find="handleFind"
                />
              </div>
            </div>
            <!--
              Bluetooth: ความเร็วส่ง (ต่อเครื่อง) + ข้อจำกัดตรง ๆ ตั้งแต่ตอนตั้ง ไม่ให้ไปเจอเองกลางกะ
              ความเร็วต้องให้คนเลือกเอง — ส่งเร็วเกินที่เครื่องรับได้ = พิมพ์เพี้ยน/ขาด และไม่มี API
              ไหนบอกเราได้ จึงวัดด้วยใบทดสอบ (toast บอกวินาที) แล้วปรับขึ้น/ลง
            -->
            <div v-if="isBt" class="px-3 pb-3">
              <div class="bg-body-tertiary rounded-3 px-3 py-2">
                <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <span class="small fw-semibold"><i class="fa-solid fa-gauge me-1"/>{{ $t('_printer.Send speed') }}</span>
                  <div class="btn-group btn-group-sm ms-auto" role="group">
                    <template v-for="opt in speedOptions" :key="opt.value">
                      <input
                        :id="'btSpeed-' + opt.value"
                        v-model="form.btSpeed"
                        type="radio"
                        class="btn-check"
                        name="printerBtSpeed"
                        :value="opt.value"
                        autocomplete="off"
                      >
                      <label class="btn btn-outline-theme" :for="'btSpeed-' + opt.value">{{ $t(opt.label) }}</label>
                    </template>
                  </div>
                </div>
                <div class="small text-muted">
                  {{ $t('_printer.If it prints garbled or stops midway - turn the printer off and on then step the speed back down') }}
                </div>
                <!-- ใบทดสอบปกติสั้น (~14 KB) ยิงไม่นานพอจะล้นคิว — ระดับที่ผ่านใบสั้นยังอาจทิ้งข้อมูลกลางบิลจริง -->
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 mt-1 text-theme text-decoration-none fw-semibold"
                  :disabled="busy"
                  @click="handleTestPrint(true)"
                >
                  <i class="fa-solid fa-receipt me-1"/>{{ $t('_printer.Print a long test bill to check this speed') }}
                </button>
                <div v-if="bt.connected && bt.ackWrites" class="small text-warning-emphasis mt-1">
                  <i class="fa-solid fa-hourglass-half me-1"/>{{ $t('_printer.This printer confirms every chunk it receives so it stays slow at any speed') }}
                </div>
                <div v-if="!rememberBt" class="small text-muted mt-1">
                  <i class="fa-solid fa-rotate me-1"/>{{ $t('_printer.This browser cannot remember a Bluetooth printer - press Connect once each time the app is opened') }}
                </div>
              </div>
            </div>
            <div class="border-top px-3 py-2 d-flex align-items-center">
              <button
                v-if="isBt && !bt.connected"
                type="button"
                class="btn btn-link btn-sm text-muted text-decoration-none p-0"
                @click="toggleChange"
              >
                {{ $t('_printer.Change printer') }}
                <i v-if="findOptions.length > 1" class="fa-solid ms-1" :class="showChange ? 'fa-chevron-up' : 'fa-chevron-down'"/>
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm text-muted text-decoration-none p-0 ms-auto"
                @click="form.transport = 'none'"
              >
                <i class="fa-regular fa-file-pdf me-1"/>{{ $t('_printer.Stop using a printer on this device - use PDF') }}
              </button>
            </div>
          </template>
        </div>

        <!-- 2. ขนาดกระดาษ — ใช้ทั้งทางพิมพ์ตรงและทาง PDF -->
        <div v-if="form.transport" class="form-card">
          <div class="form-section-header">
            <i class="fa-solid fa-receipt"/>
            <span>{{ $t('_printer.Paper size') }}</span>
          </div>
          <div class="p-3">
            <div class="row g-2">
              <div
                v-for="opt in paperOptions"
                :key="opt.value"
                class="col-6"
              >
                <label
                  class="option-card choice-tile h-100"
                  :class="{ 'is-checked': form.paperWidth === opt.value }"
                >
                  <input
                    v-model="form.paperWidth"
                    class="visually-hidden"
                    type="radio"
                    name="printerPaperWidth"
                    :value="opt.value"
                    @change="detectedFrom = ''"
                  >
                  <i
                    v-if="form.paperWidth === opt.value"
                    class="fa-solid fa-circle-check text-theme choice-tile__check"
                  />
                  <!-- ม้วนกระดาษจำลอง กว้างตามสัดส่วนจริง 80:58 ให้เห็นต่างก่อนอ่านตัวเลข -->
                  <div
                    class="paper-art"
                    :class="form.paperWidth === opt.value ? 'text-theme' : 'text-secondary'"
                    :style="{ width: opt.artWidth + 'px' }"
                  >
                    <span/><span/><span class="short"/>
                  </div>
                  <div class="fw-semibold">{{ opt.value }}</div>
                  <div class="choice-tile__hint text-muted">{{ $t(opt.hint) }}</div>
                </label>
              </div>
            </div>
            <div v-if="detectedFrom" class="small text-theme mt-2">
              <i class="fa-solid fa-wand-magic-sparkles me-1"/>
              {{ $t('_printer.Detected from the printer name') }}: {{ detectedFrom }}
            </div>
          </div>
        </div>

        <!-- 3. ลิ้นชัก — เสียบสายอยู่กับเครื่องพิมพ์ จึงมีได้เฉพาะเครื่องที่ต่อเครื่องพิมพ์ตรง -->
        <div v-if="isDirect" class="form-card">
          <div class="form-section-header">
            <i class="fa-solid fa-cash-register"/>
            <span>{{ $t('_printer.Cash drawer') }}</span>
          </div>
          <label class="d-flex align-items-center gap-3 p-3 mb-0 cursor-pointer" for="printerCashDrawer">
            <div class="flex-grow-1">
              <div class="fw-semibold">{{ $t('_printer.A cash drawer is plugged into this printer') }}</div>
              <div class="small text-muted">
                {{ $t('_printer.The drawer opens by itself on cash payments and an Open Drawer button appears on the Service Sessions page') }}
              </div>
            </div>
            <div class="form-check form-switch m-0 option-switch">
              <input
                id="printerCashDrawer"
                v-model="form.cashDrawer"
                class="form-check-input shadow-none"
                type="checkbox"
                role="switch"
              >
            </div>
          </label>
        </div>

        <p v-if="isDirect" class="small text-muted mb-0 px-1">
          <i class="fa-regular fa-lightbulb me-1"/>
          {{ $t('_printer.The test page has a border across the full paper width - a cut-off right edge or a wide blank strip means the paper size is wrong') }}
        </p>
      </div>

      <div class="offcanvas-footer bg-light border-top d-flex align-items-center gap-2 px-3 py-2">
        <div class="small text-success me-auto saved-flag" :class="{ show: justSaved }">
          <i class="fa-solid fa-check me-1"/>{{ $t('_printer.Saved on this device') }}
        </div>
        <button
          v-if="isDirect && form.cashDrawer"
          type="button"
          class="btn btn-sm btn-outline-secondary rounded-3"
          :disabled="busy"
          @click="handleTestDrawer"
        >
          <i class="fa-solid fa-cash-register me-1"/>{{ $t('Open Drawer') }}
        </button>
        <button
          v-if="isDirect"
          type="button"
          class="btn btn-sm btn-theme rounded-3 fw-semibold"
          :disabled="busy"
          @click="handleTestPrint"
        >
          <i :class="busy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-print'" class="me-1"/>
          {{ $t('_printer.Print test page') }}
        </button>
        <button
          v-else
          type="button"
          class="btn btn-sm btn-light border-dark-subtle rounded-3"
          data-bs-dismiss="offcanvas"
        >
          {{ $t('Close') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import moment from 'moment';
import { printerSettings, savePrinterSettings, guessPaperWidth } from '@/utils/printerSettings';
import {
  canPair,
  selectPrinter,
  getPairedPrinter,
  printerDisplayName,
  onPrinterConnectionChange,
  printCanvas,
  openCashDrawer,
  renderTestSlipCanvas,
  dotsForPaperWidth
} from '@/utils/escposPrinter';
import {
  bluetoothStatus,
  canUseBluetooth,
  canRememberBluetooth,
  bluetoothAdapterAvailable,
  onBluetoothAvailabilityChange,
  hasBluetoothHandle,
  requestBluetoothPrinter,
  reconnectBluetoothPrinter
} from '@/utils/bluetoothPrinter';
import { useScrollRestore } from '@/composables/useScrollRestore';
import FindPrinterButtons from './FindPrinterButtons.vue';

export default {
  name: 'OffcanvasPrinterSettings',
  components: { FindPrinterButtons },
  emits: ['closed'],
  data() {
    return {
      // เครื่องที่ยังไม่เคยตั้ง = ยังไม่เลือกอะไร (ไม่เดาให้) — เปิดแล้วปิดไปเฉย ๆ
      // ต้องไม่ถูกนับว่าตั้งแล้ว ไม่งั้นหน้าต่างถามตั้งค่าตอนเข้า POS จะหายไปเงียบ ๆ
      form: {
        transport: printerSettings.configured ? printerSettings.transport : null,
        paperWidth: printerSettings.paperWidth,
        cashDrawer: printerSettings.cashDrawer,
        btSpeed: printerSettings.btSpeed,
      },
      bt: bluetoothStatus,
      rememberBt: canRememberBluetooth(),
      // มี Bluetooth จริงไหม — null = ยังเช็คไม่เสร็จ (ให้กดได้ไปก่อน), false = ไม่มี/ปิดอยู่
      btAdapter: null,
      unsubscribeBt: null,
      pairedName: '', // USB เท่านั้น — Bluetooth อ่านจาก bt.name
      // ชื่อเครื่องที่ใช้เดาขนาดกระดาษรอบล่าสุด — โชว์ให้เห็นว่าระบบเลือกให้ ไม่ใช่ค่าที่ค้างมา
      detectedFrom: '',
      busy: false,
      showChange: false, // แผงปุ่มค้นหาใต้เครื่องที่ต่ออยู่ (เปลี่ยนเครื่อง)
      justSaved: false,
      savedTimer: null,
      unsubscribe: null,
    };
  },
  computed: {
    isDirect() {
      return this.form.transport === 'usb' || this.form.transport === 'bluetooth';
    },
    isBt() {
      return this.form.transport === 'bluetooth';
    },
    // ปุ่มค้นหาเรียงตามความน่าใช้: USB ก่อน (เร็ว/เสถียร) แล้วค่อย Bluetooth — มีเฉพาะที่เบราว์เซอร์นี้ทำได้
    findOptions() {
      const list = [];
      if (canPair()) list.push({ kind: 'usb', icon: 'fa-brands fa-usb', label: '_printer.Find USB printer' });
      if (canUseBluetooth()) {
        // PC ไม่มี Bluetooth / ปิดอยู่ → ปุ่มยังโชว์แต่กดไม่ได้ พร้อมเหตุผล (FindPrinterButtons)
        list.push({ kind: 'bluetooth', icon: 'fa-brands fa-bluetooth-b', label: '_printer.Find Bluetooth printer', disabled: this.btAdapter === false });
      }
      return list;
    },
    device() {
      if (this.isBt) {
        return {
          icon: 'fa-brands fa-bluetooth-b',
          name: this.bt.name,
          online: this.bt.connected,
          statusText: this.bt.connected ? '_printer.Connected by Bluetooth' : '_printer.Not connected',
          // วิธีแก้ยาว ๆ ไปอยู่ที่แถบสถานะด้านบน แถวอุปกรณ์เหลือแค่สถานะสั้น ๆ ไม่ให้ห่อบรรทัดชนปุ่ม
          hint: '_printer.Not connected - turn on the printer and press Connect',
        };
      }
      return {
        icon: 'fa-brands fa-usb',
        name: this.pairedName,
        online: !!this.pairedName,
        statusText: this.pairedName ? '_printer.Connected by USB' : '_printer.Not connected',
        hint: '_printer.Plug in and turn on the printer then press Find printer',
      };
    },
    speedOptions() {
      return [
        { value: 'normal', label: '_printer.Normal' },
        { value: 'fast', label: '_printer.Fast' },
      ];
    },
    paperOptions() {
      return [
        { value: '80mm', artWidth: 44, hint: '_printer.Standard receipt printer such as POS-80' },
        { value: '58mm', artWidth: 32, hint: '_printer.Small or portable printer' },
      ];
    },
    drawerLabel() {
      return this.form.cashDrawer ? this.$t('_printer.With cash drawer') : this.$t('_printer.No cash drawer');
    },
    status() {
      if (!this.form.transport) {
        return {
          classes: 'bg-warning-subtle text-warning-emphasis',
          icon: 'fa-solid fa-triangle-exclamation',
          title: '_printer.This device has not been set up yet',
          sub: this.$t('_printer.Find the printer below or choose to use PDF'),
        };
      }
      if (this.form.transport === 'none') {
        return {
          classes: 'bg-body-tertiary text-body-secondary border',
          icon: 'fa-regular fa-file-pdf',
          title: '_printer.No printer - receipts open as PDF',
          sub: this.$t('_printer.Paper size') + ' ' + this.form.paperWidth,
        };
      }
      if (!this.device.online) {
        return {
          classes: 'bg-danger-subtle text-danger-emphasis',
          icon: this.isBt ? 'fa-brands fa-bluetooth-b' : 'fa-solid fa-plug-circle-xmark',
          title: this.isBt ? '_printer.Bluetooth printer not connected' : '_printer.Printer not connected',
          sub: this.$t(this.device.hint),
        };
      }
      return {
        classes: 'bg-success-subtle text-success-emphasis',
        icon: 'fa-solid fa-circle-check',
        title: '_printer.Ready to print',
        sub: [this.device.name, this.form.paperWidth, this.drawerLabel].join(' · '),
      };
    },
  },
  watch: {
    form: {
      deep: true,
      handler(value) {
        if (!value.transport) return;
        savePrinterSettings({ ...value });
        this.flashSaved();
      },
    },
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => this.$emit('closed'));
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));

    this.refreshPaired();
    // ตามสายที่เสียบ/ถอด — สถานะ "เชื่อมต่อแล้ว" ต้องไม่ค้างตอนดึงสายออก
    this.unsubscribe = onPrinterConnectionChange(() => this.refreshPaired());
    bluetoothAdapterAvailable().then((available) => { this.btAdapter = available; });
    // เสียบ/ถอด USB Bluetooth dongle ระหว่างเปิดหน้านี้
    this.unsubscribeBt = onBluetoothAvailabilityChange((available) => { this.btAdapter = available; });
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.unsubscribeBt) this.unsubscribeBt();
    clearTimeout(this.savedTimer);
    $(this.$refs.offcanvas).off('hidden.bs.offcanvas');
  },
  methods: {
    async refreshPaired() {
      this.pairedName = printerDisplayName(await getPairedPrinter());
    },
    // มีทางค้นหาแค่ทางเดียว (เช่น เบราว์เซอร์ที่มีแต่ USB) → เปิดหน้าต่างค้นหาเลย ไม่ต้องกางแผง
    // ให้กดซ้ำอีกที · ต้องเรียกตรงในจังหวะที่กด (Chrome ต้องการ user gesture)
    toggleChange() {
      if (this.findOptions.length === 1 && !this.findOptions[0].disabled) {
        this.handleFind(this.findOptions[0].kind);
        return;
      }
      this.showChange = !this.showChange;
    },
    flashSaved() {
      this.justSaved = true;
      clearTimeout(this.savedTimer);
      this.savedTimer = setTimeout(() => { this.justSaved = false; }, 2000);
    },
    /**
     * ค้นหา = หน้าต่างเลือกอุปกรณ์ของ Chrome (เว็บมองไม่เห็นรายชื่อเอง) → เจอแล้วตั้งช่องทางให้เอง
     * ขนาดกระดาษเดาจากชื่อรุ่น (POS80 → 80mm, POS58/XP-58 → 58mm) เปลี่ยนเครื่องจาก 80 เป็น 58
     * ก็ตามให้เอง — ชื่อไม่บอกขนาดก็คงค่าเดิม ให้คนเลือกเอง
     * ต้องเรียก requestDevice ทันทีในจังหวะที่กด ห้าม await อะไรก่อน (Chrome ต้องการ user gesture)
     */
    async handleFind(kind) {
      this.busy = true;
      try {
        const picked = kind === 'bluetooth' ? await requestBluetoothPrinter() : await selectPrinter();
        const name = kind === 'bluetooth' ? (picked.name || 'Bluetooth') : printerDisplayName(picked);
        const guessed = guessPaperWidth(name);
        if (kind === 'usb') this.pairedName = name;
        this.form.transport = kind;
        if (guessed) this.form.paperWidth = guessed;
        this.detectedFrom = guessed ? name : '';
        this.showChange = false;
        this.$toast.success(this.$t('Printer selected') + ': ' + name);
      } catch (e) {
        // PC ไม่มี Bluetooth: Chrome ตอบ NotFoundError ชื่อเดียวกับตอนกดปิดหน้าต่างเลือก
        // แยกด้วยข้อความ ไม่งั้นกดแล้วเงียบ คนงงว่าปุ่มเสีย
        if (e && e.name === 'NotFoundError' && kind === 'bluetooth' && /adapter/i.test(e.message || '')) {
          this.btAdapter = false;
          this.$toast.error(this.$t('_printer.This computer has no Bluetooth or it is turned off'));
          return;
        }
        if (e && e.name === 'NotFoundError') return; // ปิดหน้าต่างเลือกอุปกรณ์
        if (e && e.name === 'NotAPrinterError') {
          this.$toast.error(this.$t('_printer.This Bluetooth device cannot be used as a receipt printer'));
          return;
        }
        this.$toast.error(this.$t(kind === 'bluetooth' ? '_printer.Find Bluetooth printer' : '_printer.Find USB printer') + ': ' + (e.message || e));
      } finally {
        this.busy = false;
      }
    },
    /**
     * เชื่อมต่อ Bluetooth ที่หลุด — มี handle ในหน้านี้/เบราว์เซอร์จำได้ = ต่อใหม่ได้เลยไม่ต้องเลือก
     * ไม่งั้นต้องเปิดหน้าต่างเลือกของ Chrome (เรียกทันที ไม่ await อะไรก่อน เพราะต้องใช้ gesture)
     */
    async handleConnectBt() {
      this.busy = true;
      try {
        if (hasBluetoothHandle() || this.rememberBt) {
          if (await reconnectBluetoothPrinter()) return;
        }
        await requestBluetoothPrinter();
      } catch (e) {
        if (e && e.name === 'NotFoundError') return;
        this.$toast.error(this.$t('_printer.Bluetooth printer not connected') + ': ' + (e.message || e));
      } finally {
        this.busy = false;
      }
    },
    /**
     * long = ใบยาว ~20cm ขนาดข้อมูลเท่าบิลจริง (~115 KB ที่ 80mm) ไว้ยืนยันความเร็ว Bluetooth —
     * ใบสั้นยิงไม่นานพอจะล้นคิว · เทียบ === true เพราะปุ่มท้าย offcanvas ส่ง MouseEvent มาเป็น arg
     */
    async handleTestPrint(long) {
      const isLong = long === true;
      this.busy = true;
      try {
        const lines = [
          this.$t('_printer.Paper size') + ': ' + this.form.paperWidth,
          this.$t('_printer.Cash drawer') + ': ' + (this.form.cashDrawer ? this.$t('Yes') : this.$t('No')),
          moment().format('DD/MM/YYYY HH:mm'),
        ];
        // ใบยาว = รายการ + บล็อกดำแบบ QR (ส่วนที่ทำให้หัวพิมพ์ช้าลง) ให้หนักอย่างน้อยเท่าใบเสร็จจริง
        // บรรทัด "จบบิลทดสอบ" อยู่ท้ายสุด ไว้ดูบนกระดาษว่าข้อมูลไม่หายกลางทาง
        if (isLong) {
          for (let i = 1; i <= 30; i++) lines.push(this.$t('_printer.Test item') + ' ' + i + ' ........ ' + (40 + i) + '.00');
        }
        const canvas = await renderTestSlipCanvas({
          heading: this.$t('_printer.Printer test'),
          lines,
          widthPx: dotsForPaperWidth(this.form.paperWidth),
          dense: isLong,
          footer: isLong ? this.$t('_printer.End of test bill') : '',
        });
        // ใบทดสอบไม่ใช่การรับเงิน — ทดสอบลิ้นชักแยกปุ่ม
        const sent = await printCanvas(canvas, { cashDrawer: false });
        this.reportSpeed(sent, isLong);
        if (!this.isBt) await this.refreshPaired();
      } catch (e) {
        if (e && e.name === 'NotFoundError') return;
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      } finally {
        this.busy = false;
      }
    },
    // บอกความเร็วจริงหลังพิมพ์ทดสอบ + ประมาณเวลาบิลยาว (~20cm = 1600 แถว) คนจะได้เทียบก่อน/หลังปรับ
    reportSpeed(sent, isLong) {
      if (!sent || !sent.ms) return;
      if (isLong) {
        this.$toast.info(this.$t('_printer.Long test bill sent {kb} KB in {sec} seconds - check that it printed all the way to the end line', {
          kb: (sent.bytes / 1024).toFixed(0),
          sec: (sent.ms / 1000).toFixed(1),
        }));
        return;
      }
      const bytesPerMs = sent.bytes / sent.ms;
      const longBillBytes = 1600 * (dotsForPaperWidth(this.form.paperWidth) / 8);
      this.$toast.info(this.$t('_printer.Sent {kb} KB in {sec} seconds - a long bill takes about {est} seconds', {
        kb: (sent.bytes / 1024).toFixed(1),
        sec: (sent.ms / 1000).toFixed(1),
        est: Math.max(1, Math.round(longBillBytes / bytesPerMs / 1000)),
      }));
    },
    async handleTestDrawer() {
      this.busy = true;
      try {
        await openCashDrawer();
      } catch (e) {
        if (e && e.name === 'NotFoundError') return;
        this.$toast.error(this.$t('Open Drawer') + ': ' + (e.message || e));
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
/* กว้างพอให้การ์ดตัวเลือกวางคู่กันได้ (ค่า default ของ bootstrap 400px แคบไป) */
@media (min-width: 576px) {
  .printer-offcanvas.offcanvas-end {
    width: 480px;
  }
}

.printer-status__icon {
  width: 44px;
  height: 44px;
  font-size: 1.25rem;
  background: rgba(255, 255, 255, .6);
}

.finder-icon {
  width: 64px;
  height: 64px;
  font-size: 1.6rem;
}

/* การ์ดตัวเลือก — สี/ขอบตอนเลือกมาจาก .option-card.is-checked (custom.scss) ตรงนี้แค่จัดวาง */
.choice-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
  padding: 1rem .75rem .875rem;
}
.choice-tile__hint {
  font-size: .75rem;
  line-height: 1.35;
}
.choice-tile__check {
  position: absolute;
  top: .5rem;
  right: .5rem;
}

/* ม้วนกระดาษจำลอง — เส้นขอบ/บรรทัดเป็น currentColor ให้เปลี่ยนสีตาม text-theme/text-secondary */
.paper-art {
  height: 34px;
  margin-bottom: .45rem;
  padding: 6px 5px 0;
  border: 2px solid currentColor;
  border-bottom-style: dashed;
  border-radius: 4px 4px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.paper-art span {
  display: block;
  height: 2px;
  border-radius: 1px;
  background: currentColor;
  opacity: .55;
}
.paper-art span.short {
  width: 60%;
}

.device-avatar {
  width: 44px;
  height: 44px;
  font-size: 1.15rem;
}
.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

/* สวิตช์ลิ้นชักให้ใหญ่ขึ้นหน่อย กดง่ายบนแท็บเล็ต */
.option-switch .form-check-input {
  width: 2.6em;
  height: 1.4em;
  cursor: pointer;
}

.saved-flag {
  opacity: 0;
  transition: opacity .2s ease;
}
.saved-flag.show {
  opacity: 1;
}
</style>
